import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { TranslationPaths } from "@/composables/useTranslation"
import type { TabControlItem } from "@/types"
import { computed, ref, watch } from "vue"
import { useTranslation } from "@/composables/useTranslation"
import { GuestPageTab } from "@/types"

interface GuestMainTabDefinition {
  id: GuestPageTab
  labelKey: TranslationPaths
  icon: IconKeys
}

export const GUEST_EVENT_MAIN_TABS: readonly GuestMainTabDefinition[] = [
  {
    id: GuestPageTab.gallery,
    labelKey: "gallery_floating_actions.gallery_shortcut",
    icon: "Images",
  },
  {
    id: GuestPageTab.guestbook,
    labelKey: "gallery_floating_actions.guestbook_shortcut",
    icon: "BookOpen",
  },
]

function isGuestMainTab(value: unknown): value is GuestPageTab {
  return GUEST_EVENT_MAIN_TABS.some(tab => tab.id === value)
}

export interface UseGuestEventSectionOptions {
  queryParams: Record<string, unknown>
  queryKey?: string
}

export function useGuestEventSection({
  queryParams,
  queryKey = "tab",
}: UseGuestEventSectionOptions) {
  const { t } = useTranslation()

  const activeTab = ref<GuestPageTab>(GuestPageTab.gallery)

  watch(
    () => queryParams[queryKey],
    (tab) => {
      if (isGuestMainTab(tab)) {
        activeTab.value = tab
        return
      }

      if (typeof tab === "string")
        delete queryParams[queryKey]

      activeTab.value = GuestPageTab.gallery
    },
    { immediate: true },
  )

  watch(activeTab, (tab) => {
    queryParams[queryKey] = tab
  })

  const mainSectionTabs = computed<TabControlItem[]>(() =>
    GUEST_EVENT_MAIN_TABS.map(tab => ({
      id: tab.id,
      label: t(tab.labelKey),
      icon: tab.icon,
    })),
  )

  return {
    activeTab,
    mainSectionTabs,
  }
}
